import React, {
  createContext, useContext, useState, useCallback, useEffect, ReactNode,
} from 'react';
import type { Tournament, Player, Match } from '@shootout-comps/types';
import type { ConnectionStatus } from '../socket/createSocket';
import { useAuth } from '../auth/AuthContext';

export interface TournamentSummary {
  _id: string;
  name: string;
  status: string;
  players: Player[];
  ownerId: string;
  maxPlayers?: number;
  scheduledAt?: string | null;
  isHandicap?: boolean;
  isPublic?: boolean;
  format?: 'shootout' | 'snooker900' | 'standard';
  createdAt: string;
  updatedAt: string;
}

export interface AllPlayer {
  _id: string;
  name: string;
  profilePictureUrl?: string;
  handicap: number;
  tournaments: string[];
  wins: number;
  losses: number;
  // Every distinct underlying PlayerModel id sharing this name (including _id above). Length
  // > 1 means this row is actually several separate documents for the same person — pass all
  // of them to mergePlayers to consolidate.
  mergeableIds: string[];
  // Average seconds per shot across every completed match this player has played (each match's
  // own final-frame shots, same scoping already used for this stat on the historic match page).
  // null when they have no recorded shots yet.
  avgShotTimeSeconds: number | null;
}

export interface PlayerMatchRecord {
  tournamentId:   string;
  tournamentName: string;
  roundName:      string;
  matchId:        string;
  opponent:       { _id: string; name: string; profilePictureUrl?: string } | null;
  playerScore:    number | null;
  opponentScore:  number | null;
  result:         'win' | 'loss';
  playedAt:       string | null;
}

// Shape returned by GET /public-tournaments — a lighter projection than TournamentSummary
// (no player photos, since it's cross-user and those are full base64 data URLs), plus the
// host's username since the viewer isn't the owner.
export interface PublicTournamentSummary {
  _id: string;
  name: string;
  status: string;
  players: { _id: string; name: string; seed: number; isBye?: boolean }[];
  ownerId: string;
  ownerUsername: string | null;
  maxPlayers?: number;
  scheduledAt?: string | null;
  isHandicap?: boolean;
  sixReds?: boolean;
  format?: 'shootout' | 'snooker900' | 'standard';
  createdAt: string;
  updatedAt: string;
}

export interface TournamentSettingsInput {
  maxPlayers?: number;
  scheduledAt?: string | null;
  isHandicap?: boolean;
  location?: string | null;
  entryFee?: number | null;
  currency?: string;
  prizes?: { place: number; amount: number }[];
  highBreakPrize?: number | null;
  format?: 'shootout' | 'snooker900' | 'standard';
  sixReds?: boolean;
  isPublic?: boolean;
  bestOfByRound?: number[] | null;
}

export interface TournamentAdmins {
  owner: { _id: string; username: string };
  admins: { _id: string; username: string }[];
}

interface TournamentContextValue {
  // The tournament currently open on screen — set only by the explicit ID-scoped calls
  // below, never resolved implicitly. A user may own any number of tournaments; this is
  // just whichever one is currently being viewed/managed.
  tournament: Tournament | null;
  loading: boolean;
  error: string | null;
  fetchTournament: (id: string) => Promise<Tournament>;
  // Clears `tournament` when the caller navigates away from whatever tournament was open —
  // keeps this state from going stale once nothing is displaying it anymore.
  clearOpenTournament: () => void;
  createTournament: (paymentToken?: string) => Promise<Tournament>;
  cancelTournament: (id: string) => Promise<void>;
  addPlayer: (tournamentId: string, name: string, profilePicture?: File, existingPictureUrl?: string, handicap?: number, existingPlayerId?: string) => Promise<Player>;
  removePlayer: (tournamentId: string, playerId: string) => Promise<void>;
  updatePlayerPicture: (tournamentId: string, playerId: string, file: File) => Promise<void>;
  startTournament: (id: string) => Promise<Tournament>;
  getPaymentStatus: (format?: string) => Promise<{ paymentRequired: boolean; amountPence?: number; currency?: string }>;
  confirmPayment: (stripeSessionId: string) => Promise<{ paymentToken: string }>;
  updateName: (id: string, name: string) => Promise<void>;
  updateSettings: (id: string, settings: TournamentSettingsInput) => Promise<void>;
  updatePlayerHandicap: (tournamentId: string, playerId: string, handicap: number) => Promise<void>;
  pauseAllClocks: (id: string) => Promise<void>;
  resumeAllClocks: (id: string) => Promise<void>;
  startMatch: (matchId: string) => Promise<Match>;
  endMatch: (matchId: string, winnerId?: string) => Promise<Match>;
  fetchMatchTournamentFormat: (matchId: string) => Promise<{ _id: string; format: string }>;
  fetchTournamentHistory: () => Promise<TournamentSummary[]>;
  fetchHistoricTournament: (id: string) => Promise<Tournament>;
  fetchHistoricMatch: (tournamentId: string, matchId: string) => Promise<Match>;
  fetchPublicTournaments: () => Promise<PublicTournamentSummary[]>;
  fetchPublicTournament: (id: string) => Promise<Tournament>;
  fetchPublicMatch: (tournamentId: string, matchId: string) => Promise<Match>;
  fetchAllPlayers: () => Promise<AllPlayer[]>;
  fetchPlayerMatches: (id: string) => Promise<PlayerMatchRecord[]>;
  updatePlayerGlobal: (id: string, dataUrl?: string, handicap?: number, name?: string) => Promise<void>;
  deletePlayerGlobal: (id: string) => Promise<void>;
  // Consolidates several same-named PlayerModel documents into one. `ids[0]` is kept as the
  // surviving id — pass the currently-displayed _id first so the merge matches what's on screen.
  mergePlayers: (ids: string[]) => Promise<{ _id: string; name: string; mergedIds: string[] }>;
  // Tournament admins — owner-equivalent day-to-day access for other users, added by
  // username. Viewable by owner or any admin; adding/removing is owner-only except that any
  // admin may remove themselves (see apps/api/src/routes/tournament.routes.ts).
  fetchTournamentAdmins: (id: string) => Promise<TournamentAdmins>;
  addTournamentAdmin: (id: string, username: string) => Promise<TournamentAdmins>;
  removeTournamentAdmin: (id: string, userId: string) => Promise<TournamentAdmins>;
}

const TournamentContext = createContext<TournamentContextValue | null>(null);

export interface TournamentProviderProps {
  children: ReactNode;
  // Base URL to prefix onto '/api' — '' for web (same-origin relative requests),
  // an absolute origin (e.g. 'https://api.example.com') for mobile.
  apiBaseUrl: string;
  useTournamentSocket: (tournamentId: string | null, handlers: Record<string, (data: unknown) => void>) => void;
  useConnectionStatus: () => ConnectionStatus;
}

export function TournamentProvider({ children, apiBaseUrl, useTournamentSocket, useConnectionStatus }: TournamentProviderProps) {
  const API = `${apiBaseUrl}/api`;
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const { getAccessToken, isAuthenticated } = useAuth();

  // ── Socket: join the room for whichever tournament is currently open ─────
  useTournamentSocket(tournament ? String(tournament._id) : null, {
    'tournament:updated': (data: unknown) => setTournament(data as Tournament),
  });

  const { reconnectCount } = useConnectionStatus();

  // ── Core fetch helper ─────────────────────────────────────────────────────
  const jsonRequest = useCallback(async <T,>(
    path: string, options: RequestInit = {},
  ): Promise<T> => {
    setLoading(true); setError(null);
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(`${API}${path}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(options.headers as Record<string, string> | undefined),
        },
      });
      const data = await res.json() as T & { error?: string };
      if (!res.ok) throw new Error(data.error ?? 'Request failed');
      return data;
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Unknown error';
      setError(msg); throw e;
    } finally {
      setLoading(false);
    }
  }, [API, getAccessToken]);

  // ── Tournament actions ────────────────────────────────────────────────────

  const fetchTournament = useCallback(async (id: string): Promise<Tournament> => {
    const data = await jsonRequest<Tournament>(`/tournaments/${id}`);
    setTournament(data);
    return data;
  }, [jsonRequest]);

  // ── Reconnect resync: a dropped connection means any tournament:updated events fired
  // during the gap were missed — refetch once per reconnect to catch up. Swallow errors
  // deliberately, a transient refetch failure right after reconnecting shouldn't blow away
  // an already-loaded bracket.
  useEffect(() => {
    if (reconnectCount > 0 && tournament) fetchTournament(String(tournament._id)).catch(() => null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reconnectCount]);

  const createTournament = useCallback(async (paymentToken?: string): Promise<Tournament> => {
    const data = await jsonRequest<Tournament>('/tournaments', {
      method: 'POST',
      body: JSON.stringify({ paymentToken }),
    });
    setTournament(data);
    return data;
  }, [jsonRequest]);

  const clearOpenTournament = useCallback(() => setTournament(null), []);

  const cancelTournament = useCallback(async (id: string): Promise<void> => {
    await jsonRequest(`/tournaments/${id}`, { method: 'DELETE' });
    setTournament(prev => (prev && String(prev._id) === id ? null : prev));
  }, [jsonRequest]);

  // Clear tournament state on sign-out
  useEffect(() => {
    if (!isAuthenticated) setTournament(null);
  }, [isAuthenticated]);

  const addPlayer = useCallback(async (tournamentId: string, name: string, profilePicture?: File, existingPictureUrl?: string, handicap?: number, existingPlayerId?: string): Promise<Player> => {
    setLoading(true); setError(null);
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Not authenticated');
      const formData = new FormData();
      formData.append('tournamentId', tournamentId);
      formData.append('name', name);
      if (profilePicture) formData.append('profilePicture', profilePicture);
      if (!profilePicture && existingPictureUrl) formData.append('existingPictureUrl', existingPictureUrl);
      if (handicap !== undefined) formData.append('handicap', String(handicap));
      if (existingPlayerId) formData.append('existingPlayerId', existingPlayerId);
      const res = await fetch(`${API}/players`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json() as Player & { error?: string };
      if (!res.ok) throw new Error(data.error ?? 'Failed to add player');
      // The endpoint only returns the new player, not the updated tournament — normally
      // the tournament:updated socket broadcast fills that gap, but don't rely solely on
      // realtime delivery for the client that just made the change.
      await fetchTournament(tournamentId);
      return data;
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Unknown error';
      setError(msg); throw e;
    } finally {
      setLoading(false);
    }
  }, [API, getAccessToken, fetchTournament]);

  const removePlayer = useCallback(async (tournamentId: string, playerId: string) => {
    await jsonRequest(`/players/${playerId}?tournamentId=${tournamentId}`, { method: 'DELETE' });
    // Same reasoning as addPlayer — don't rely solely on the tournament:updated broadcast
    // to reflect this client's own change.
    await fetchTournament(tournamentId);
  }, [jsonRequest, fetchTournament]);

  const updatePlayerPicture = useCallback(async (tournamentId: string, playerId: string, file: File): Promise<void> => {
    setLoading(true); setError(null);
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Not authenticated');
      const form = new FormData();
      form.append('profilePicture', file);
      const res = await fetch(`${API}/players/${playerId}?tournamentId=${tournamentId}`, {
        method: 'PUT', headers: { Authorization: `Bearer ${token}` }, body: form,
      });
      const data = await res.json() as { error?: string };
      if (!res.ok) throw new Error(data.error ?? 'Failed');
      // Refresh tournament to pick up new picture URL
      const updated = await jsonRequest<Tournament>(`/tournaments/${tournamentId}`);
      setTournament(updated);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Unknown error';
      setError(msg); throw e;
    } finally { setLoading(false); }
  }, [API, getAccessToken, jsonRequest]);

  const startTournament = useCallback(async (id: string): Promise<Tournament> => {
    const data = await jsonRequest<Tournament>(`/tournaments/${id}/start`, { method: 'POST' });
    setTournament(data); return data;
  }, [jsonRequest]);

  const getPaymentStatus = useCallback(async (format?: string) => {
    const qs = format ? `?format=${encodeURIComponent(format)}` : '';
    return jsonRequest<{ paymentRequired: boolean; amountPence?: number; currency?: string }>(`/tournament/payment-status${qs}`);
  }, [jsonRequest]);

  const confirmPayment = useCallback(async (stripeSessionId: string) => {
    return jsonRequest<{ paymentToken: string }>('/tournament/confirm-payment', {
      method: 'POST',
      body: JSON.stringify({ stripeSessionId }),
    });
  }, [jsonRequest]);

  const updateName = useCallback(async (id: string, name: string): Promise<void> => {
    const data = await jsonRequest<Tournament>(`/tournaments/${id}/name`, {
      method: 'PUT', body: JSON.stringify({ name }),
    });
    setTournament(data);
  }, [jsonRequest]);

  const updatePlayerHandicap = useCallback(async (tournamentId: string, playerId: string, handicap: number): Promise<void> => {
    setLoading(true); setError(null);
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Not authenticated');
      const form = new FormData();
      form.append('handicap', String(handicap));
      const res = await fetch(`${API}/players/${playerId}?tournamentId=${tournamentId}`, {
        method: 'PUT', headers: { Authorization: `Bearer ${token}` }, body: form,
      });
      const data = await res.json() as { error?: string };
      if (!res.ok) throw new Error(data.error ?? 'Failed');
      const updated = await jsonRequest<Tournament>(`/tournaments/${tournamentId}`);
      setTournament(updated);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Unknown error';
      setError(msg); throw e;
    } finally { setLoading(false); }
  }, [API, getAccessToken, jsonRequest]);

  const updateSettings = useCallback(async (id: string, settings: TournamentSettingsInput): Promise<void> => {
    const data = await jsonRequest<Tournament>(`/tournaments/${id}/settings`, {
      method: 'PUT', body: JSON.stringify(settings),
    });
    setTournament(data);
  }, [jsonRequest]);

  const pauseAllClocks = useCallback(async (id: string): Promise<void> => {
    await jsonRequest(`/tournaments/${id}/pause-all`, { method: 'POST' });
  }, [jsonRequest]);

  const resumeAllClocks = useCallback(async (id: string): Promise<void> => {
    await jsonRequest(`/tournaments/${id}/resume-all`, { method: 'POST' });
  }, [jsonRequest]);

  const startMatch = useCallback(async (matchId: string): Promise<Match> =>
    jsonRequest<Match>(`/matches/${matchId}/start`, { method: 'POST' }),
  [jsonRequest]);

  const endMatch = useCallback(async (matchId: string, winnerId?: string): Promise<Match> =>
    jsonRequest<Match>(`/matches/${matchId}/end`, {
      method: 'POST', body: JSON.stringify({ winnerId }),
    }),
  [jsonRequest]);

  // Resolves which tournament a match belongs to (and its format) without touching the
  // `tournament` state above — this is a side-lookup for a match's own rules, independent
  // of whichever tournament the user currently has open elsewhere in the app.
  const fetchMatchTournamentFormat = useCallback(async (matchId: string): Promise<{ _id: string; format: string }> =>
    jsonRequest<{ _id: string; format: string }>(`/matches/${matchId}/tournament`),
  [jsonRequest]);

  // ── History ───────────────────────────────────────────────────────────────

  const fetchTournamentHistory = useCallback(async (): Promise<TournamentSummary[]> =>
    jsonRequest<TournamentSummary[]>('/tournaments'),
  [jsonRequest]);

  const fetchHistoricTournament = useCallback(async (id: string): Promise<Tournament> =>
    jsonRequest<Tournament>(`/tournaments/${id}`),
  [jsonRequest]);

  const fetchHistoricMatch = useCallback(async (tournamentId: string, matchId: string): Promise<Match> =>
    jsonRequest<Match>(`/tournaments/${tournamentId}/matches/${matchId}`),
  [jsonRequest]);

  // ── Public tournaments (any signed-in user, not just the owner) ────────────

  const fetchPublicTournaments = useCallback(async (): Promise<PublicTournamentSummary[]> =>
    jsonRequest<PublicTournamentSummary[]>('/public-tournaments'),
  [jsonRequest]);

  const fetchPublicTournament = useCallback(async (id: string): Promise<Tournament> =>
    jsonRequest<Tournament>(`/public-tournaments/${id}`),
  [jsonRequest]);

  const fetchPublicMatch = useCallback(async (tournamentId: string, matchId: string): Promise<Match> =>
    jsonRequest<Match>(`/public-tournaments/${tournamentId}/matches/${matchId}`),
  [jsonRequest]);

  const fetchAllPlayers = useCallback(async (): Promise<AllPlayer[]> =>
    jsonRequest<AllPlayer[]>('/players/all'),
  [jsonRequest]);

  const fetchPlayerMatches = useCallback(async (id: string): Promise<PlayerMatchRecord[]> =>
    jsonRequest<PlayerMatchRecord[]>(`/players/${id}/matches`),
  [jsonRequest]);

  const updatePlayerGlobal = useCallback(async (id: string, dataUrl?: string, handicap?: number, name?: string): Promise<void> => {
    setLoading(true); setError(null);
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Not authenticated');
      const form = new FormData();
      if (dataUrl) form.append('existingPictureUrl', dataUrl);
      if (handicap !== undefined) form.append('handicap', String(handicap));
      if (name !== undefined) form.append('name', name);
      const res = await fetch(`${API}/players/${id}/global`, {
        method: 'PUT', headers: { Authorization: `Bearer ${token}` }, body: form,
      });
      const data = await res.json() as { error?: string };
      if (!res.ok) throw new Error(data.error ?? 'Failed');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Unknown error';
      setError(msg); throw e;
    } finally { setLoading(false); }
  }, [API, getAccessToken]);

  const deletePlayerGlobal = useCallback(async (id: string): Promise<void> => {
    await jsonRequest(`/players/${id}/global`, { method: 'DELETE' });
  }, [jsonRequest]);

  const mergePlayers = useCallback(async (ids: string[]) =>
    jsonRequest<{ _id: string; name: string; mergedIds: string[] }>('/players/merge', {
      method: 'POST', body: JSON.stringify({ ids }),
    }),
  [jsonRequest]);

  const fetchTournamentAdmins = useCallback((id: string) =>
    jsonRequest<TournamentAdmins>(`/tournaments/${id}/admins`),
  [jsonRequest]);

  const addTournamentAdmin = useCallback((id: string, username: string) =>
    jsonRequest<TournamentAdmins>(`/tournaments/${id}/admins`, {
      method: 'POST', body: JSON.stringify({ username }),
    }),
  [jsonRequest]);

  const removeTournamentAdmin = useCallback((id: string, userId: string) =>
    jsonRequest<TournamentAdmins>(`/tournaments/${id}/admins/${userId}`, { method: 'DELETE' }),
  [jsonRequest]);

  return (
    <TournamentContext.Provider value={{
      tournament, loading, error,
      fetchTournament, clearOpenTournament, createTournament, cancelTournament, addPlayer, removePlayer,
      startTournament, updateName, updateSettings, pauseAllClocks, resumeAllClocks, updatePlayerPicture, updatePlayerHandicap,
      startMatch, endMatch, fetchMatchTournamentFormat,
      fetchTournamentHistory, fetchHistoricTournament, fetchHistoricMatch,
      fetchPublicTournaments, fetchPublicTournament, fetchPublicMatch,
      fetchAllPlayers, fetchPlayerMatches, updatePlayerGlobal, deletePlayerGlobal, mergePlayers,
      getPaymentStatus, confirmPayment,
      fetchTournamentAdmins, addTournamentAdmin, removeTournamentAdmin,
    }}>
      {children}
    </TournamentContext.Provider>
  );
}

export function useTournament(): TournamentContextValue {
  const ctx = useContext(TournamentContext);
  if (!ctx) throw new Error('useTournament must be used within TournamentProvider');
  return ctx;
}
